import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Primitive, Skeleton } from '@rtkwlf/fenrir-react';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faRotateBack } from '@rtkwlf/pro-solid-svg-icons/faRotateBack';
import { useQuery } from '@tanstack/react-query';

import { useGetOrganizationSpidrOptions } from '@apis/track-public/query-options';
import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';
import { useDocumentTitle } from '@packages/fenrir/hooks/use-document-title';
import { PagePlain } from '@packages/fenrir/pages/page-plain';

import { SpidrDetailsPage } from '../../../components/spidr-details-page';
import { WidgetError } from '../../../components/widget/widget-error';
import { useSpidrIDParam } from '../../../hooks/use-spidr-id-param';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';

/* -------------------------------------------------------------------------------------------------
 * Route: SpidrIDPage - For an ad-hoc SPiDR
 * -----------------------------------------------------------------------------------------------*/
export const SpidrIDPage = () => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Get spidrID param from url
   */
  const { spidrID } = useSpidrIDParam();

  /**
   * Get query options for spidr details
   */
  const {
    data: spidr,
    isLoading,
    isError,
    refetch,
  } = useQuery(useGetOrganizationSpidrOptions({ spidrID }));

  /**
   * Set the document title
   */
  useDocumentTitle(t('spidrID.documentTitle', { name: spidr?.blueprint.name }));

  return (
    <Primitive.div
      padding="medium"
      boxShadow="default"
      alignSelf="stretch"
      backgroundColor="widgetDefault"
    >
      <GeneralWrapper
        isLoading={isLoading}
        isError={isError}
        loader={
          <Skeleton
            data-testid="adhoc-spidr-loading"
            width="full"
            height="10rem"
            margin="zero"
          />
        }
        error={
          <WidgetError
            data-testid="adhoc-spidr-error"
            actionIcon={<FontAwesomeIcon icon={faRotateBack} size="sm" />}
            actionText={t('common.retry')}
            heading={t('spidrID.error.title.adhocSpidr')}
            description={t('common.clickToRetry')}
            icon={<FontAwesomeIcon icon={faExclamationTriangle} size="2x" />}
            onActionClick={() => refetch()}
          />
        }
      >
        {spidr && <SpidrDetailsPage spidr={spidr} />}
      </GeneralWrapper>
    </Primitive.div>
  );
};

export const Component = () => (
  // document title is set in SpidrIDPage
  <PagePlain>
    <SpidrIDPage />
  </PagePlain>
);
